/**
 * Quality Command Center — deep-evidence tabs. Every view renders ONLY real ingested
 * evidence and labels executed scope vs not-executed/not-captured explicitly. No mock
 * data, no synthetic trends, no extrapolation from smoke/small profiles.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle } from 'lucide-react';
import {
  useQaRunsByEngine, useQaRuns, useQaSecurityMatrix, useQaScenariosForRun, useQaFindings, useQaAuditDebt, useQaMutations,
} from '@/hooks/useQualityCommandCenter';
import {
  normResult, artifactHealthClass, buildSecurityMatrix, SECURITY_SURFACES, SECURITY_ATTACKS,
  simFidelity, comparableSeries,
} from '@/lib/qa/commandCenterLogic';
import { ResultBadge, Loading, ErrorState, Empty, Insufficient, Sparkline } from './shared';

/* ============================================================ Performance */

export function PerformanceTab() {
  const runs = useQaRunsByEngine('performance');
  if (runs.isLoading) return <Loading />;
  if (runs.error) return <ErrorState e={runs.error} />;
  const latest = runs.data?.[0];
  const perf = latest?.metrics?.performance;
  if (!perf?.benchmarks?.length) return <Empty what="performance" />;

  return (
    <div className="space-y-4">
      <Alert className="border-slate-300">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Executed scale: {perf.scale} profile ({perf.shops} shops / {perf.historyRows} history rows)</AlertTitle>
        <AlertDescription className="text-sm">
          <strong>Not validated:</strong> 1,000 shops · 10,000 shops. Enterprise readiness is <strong>not</strong> implied by this small-profile benchmark.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Benchmarks (ms) — run {latest.run_id}</CardTitle>
          <CardDescription>p50 / p99 / mean / variance / warm-up are not captured by the source harness — shown as “n/c”.</CardDescription></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>KPI</TableHead><TableHead>min</TableHead><TableHead>median (p50≈)</TableHead><TableHead>p95</TableHead><TableHead>p99</TableHead><TableHead>max</TableHead><TableHead>mean</TableHead><TableHead>variance</TableHead></TableRow></TableHeader>
            <TableBody>
              {perf.benchmarks.map((b: any) => (
                <TableRow key={b.label}>
                  <TableCell className="text-sm font-medium">{b.label}</TableCell>
                  <TableCell>{b.min ?? 'n/c'}</TableCell>
                  <TableCell>{b.median ?? 'n/c'}</TableCell>
                  <TableCell>{b.p95 ?? 'n/c'}</TableCell>
                  <TableCell className="text-muted-foreground">n/c</TableCell>
                  <TableCell>{b.max ?? 'n/c'}</TableCell>
                  <TableCell className="text-muted-foreground">n/c</TableCell>
                  <TableCell className="text-muted-foreground">n/c</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {runs.data && runs.data.length >= 2 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Run-over-run at same scale ({runs.data.length} performance runs)</CardTitle>
            <CardDescription>Compared only within the identical {perf.scale} profile — never across scales.</CardDescription></CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            {perf.benchmarks.map((b: any) => {
              const series = runs.data!.map((r) => r.metrics?.performance?.benchmarks?.find((x: any) => x.label === b.label)?.median).filter((v): v is number => typeof v === 'number');
              return <div key={b.label} className="flex items-center gap-3 py-1"><span className="w-56 shrink-0">{b.label} median</span><span className="text-primary"><Sparkline values={series.slice().reverse()} label={`${b.label} median ms`} width={160} height={28} /></span></div>;
            })}
          </CardContent>
        </Card>
      )}
      <p className="text-xs text-muted-foreground">Query-plan / realtime-convergence / browser-render metrics are not present in the ingested run metrics; see <code>performance-readiness.json</code> in source artifacts.</p>
    </div>
  );
}

/* ============================================================ Soak */

const SOAK_PROFILES = [
  { key: 'smoke', label: 'Smoke (25 iterations)' },
  { key: '30m', label: '30 minutes' },
  { key: '6h', label: '6 hours' },
  { key: '12h', label: '12 hours' },
  { key: '24h', label: '24 hours' },
];

export function SoakTab() {
  const runs = useQaRunsByEngine('soak');
  if (runs.isLoading) return <Loading />;
  if (runs.error) return <ErrorState e={runs.error} />;
  const latest = runs.data?.[0];
  const soak = latest?.metrics?.soak;
  if (!soak?.series?.length) return <Empty what="soak" />;
  const lat = soak.series.map((p: any) => p.latencyMs);
  const rss = soak.series.map((p: any) => p.rssMb);

  return (
    <div className="space-y-4">
      <Alert className="border-amber-400 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-700" />
        <AlertTitle className="text-amber-900">Smoke soak does not validate long-duration stability</AlertTitle>
        <AlertDescription className="text-amber-900/80 text-sm">Only the 25-iteration smoke profile was executed. 30m / 6h / 12h / 24h are NOT_EXECUTED and must not be read as passing.</AlertDescription>
      </Alert>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card><CardHeader className="pb-1"><CardDescription>Latency over iterations (ms)</CardDescription></CardHeader>
          <CardContent className="text-primary"><Sparkline values={lat} label="soak latency ms" width={300} height={50} />
            <div className="text-xs text-muted-foreground mt-1">median {soak.medianMs}ms · {soak.iterations} iterations</div></CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>RSS memory over iterations (MB)</CardDescription></CardHeader>
          <CardContent className="text-primary"><Sparkline values={rss} label="soak RSS MB" width={300} height={50} />
            <div className="text-xs text-muted-foreground mt-1">RSS growth {soak.rssGrowthMb}MB across run</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Duration-profile status</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Profile</TableHead><TableHead>Status</TableHead><TableHead>Evidence</TableHead></TableRow></TableHeader>
            <TableBody>
              {SOAK_PROFILES.map((p) => (
                <TableRow key={p.key}>
                  <TableCell className="text-sm">{p.label}</TableCell>
                  <TableCell><ResultBadge result={p.key === 'smoke' ? normResult(latest.result) : 'NOT_EXECUTED'} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.key === 'smoke' ? `${soak.iterations} iters, cleanup residue ${latest.residue ?? 'n/a'}` : 'not executed'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">Heap / subscription-count / WebSocket-count are not captured in the ingested soak metrics (latency + RSS series only).</p>
    </div>
  );
}

/* ============================================================ Security coverage matrix */

export function SecurityMatrixTab() {
  const matrixRows = useQaSecurityMatrix();
  const secRuns = useQaRunsByEngine('security');
  const latestSec = secRuns.data?.[0]?.run_id;
  const scenarios = useQaScenariosForRun(latestSec ?? null);

  if (secRuns.isLoading || scenarios.isLoading) return <Loading />;
  if (secRuns.error) return <ErrorState e={secRuns.error} />;
  if (!latestSec) return <Empty what="security" />;

  const cells = buildSecurityMatrix((scenarios.data ?? []).map((s: any) => ({ name: s.name, status: s.status, run_id: latestSec })));
  const cellAt = (surface: string, attack: string) => cells.find((c) => c.surface === surface && c.attack === attack);

  return (
    <div className="space-y-4">
      <Alert className="border-slate-300">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Coverage matrix — surfaces × attacks (run {latestSec})</AlertTitle>
        <AlertDescription className="text-sm">No overall “Security PASS” is shown: unexecuted surfaces (Realtime, Storage, Session/JWT) remain visible as NOT_EXECUTED. Blank cells have no scenario evidence.</AlertDescription>
      </Alert>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead className="sticky left-0 bg-background">Attack \ Surface</TableHead>{SECURITY_SURFACES.map((s) => <TableHead key={s} className="text-center">{s}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {SECURITY_ATTACKS.map((attack) => (
              <TableRow key={attack}>
                <TableCell className="text-xs font-medium sticky left-0 bg-background">{attack}</TableCell>
                {SECURITY_SURFACES.map((surface) => {
                  const c = cellAt(surface, attack);
                  return <TableCell key={surface} className="text-center">
                    {c ? (latestSec ? <Link to={`/admin/quality/runs/${latestSec}`} title={c.scenario}><ResultBadge result={c.status} compact /></Link> : <ResultBadge result={c.status} compact />) : <span className="text-muted-foreground/40" aria-label="no evidence">·</span>}
                  </TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Permission matrix (ingested from security-permission-matrix.json)</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {matrixRows.error ? <ErrorState e={matrixRows.error} /> : !matrixRows.data?.length ? <Empty what="permission-matrix" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Resource</TableHead><TableHead>Action</TableHead><TableHead>Role</TableHead><TableHead>Relationship</TableHead><TableHead>UI</TableHead><TableHead>API</TableHead><TableHead>DB</TableHead></TableRow></TableHeader>
              <TableBody>
                {matrixRows.data.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-mono">{r.resource}</TableCell><TableCell className="text-xs">{r.action}</TableCell><TableCell className="text-xs">{r.role}</TableCell><TableCell className="text-xs">{r.relationship}</TableCell>
                    <TableCell className="text-xs">{r.ui ?? '—'}</TableCell><TableCell className="text-xs">{r.api ?? '—'}</TableCell><TableCell className="text-xs">{r.db ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ============================================================ Simulation timeline */

export function SimulationTab() {
  const runs = useQaRunsByEngine('simulation');
  if (runs.isLoading) return <Loading />;
  if (runs.error) return <ErrorState e={runs.error} />;
  const latest = runs.data?.[0];
  const events = latest?.sim_events ?? [];

  return (
    <div className="space-y-4">
      <Alert className="border-amber-400 bg-amber-50">
        <AlertTriangle className="h-4 w-4 text-amber-700" />
        <AlertTitle className="text-amber-900">SIM-FIDELITY-1 — policy-mode is DIRECT DATABASE FLOW</AlertTitle>
        <AlertDescription className="text-amber-900/80 text-sm">
          These steps are raw DB state transitions in policy mode. They are <strong>not</strong> equivalent to a real browser flow, OptiPlace assignment, or manager workflow. Manager reassignment is trigger-overridden and remains NOT_EXECUTED.
        </AlertDescription>
      </Alert>

      {!events.length ? <Empty what="simulation event" /> : (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Event timeline — run {latest!.run_id} ({events.length} events)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Sim time</TableHead><TableHead>Action (actor.action)</TableHead><TableHead>Tickets</TableHead><TableHead>Invariants</TableHead><TableHead>Fidelity</TableHead></TableRow></TableHeader>
              <TableBody>
                {events.map((ev, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-mono">{ev.t ? new Date(ev.t).toLocaleTimeString() : '—'}</TableCell>
                    <TableCell className="text-sm">{ev.label ?? ev.kind ?? '—'}</TableCell>
                    <TableCell className="text-xs">{ev.tickets ?? '—'}</TableCell>
                    <TableCell><ResultBadge result={(ev.invariantViolations ?? 0) === 0 ? 'PASS' : 'FAIL_PRODUCT'} /></TableCell>
                    <TableCell><Badge variant="outline" className="border-blue-300 text-blue-700 text-[10px]">{simFidelity(ev)}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground mt-2">Before/after row state and actor/target are not captured per event by the source harness — the label encodes actor+action (e.g. <code>manager-assigns-#0</code>).</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ============================================================ Artifact health */

export function ArtifactHealthTab() {
  const runs = useQaRuns();
  if (runs.isLoading) return <Loading />;
  if (runs.error) return <ErrorState e={runs.error} />;
  if (!runs.data?.length) return <Empty what="run" />;

  const rows = runs.data.map((r) => ({ run: r, h: r.artifact_health, cls: artifactHealthClass(r.artifact_health) }));
  const errors = rows.filter((x) => x.cls === 'ARTIFACT_ERROR');
  const incomplete = rows.filter((x) => x.cls === 'NOT_EXECUTED');

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card><CardHeader className="pb-1"><CardDescription>Runs with artifact errors</CardDescription><CardTitle className="text-2xl">{errors.length}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">malformed evidence — affects posture</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>Incomplete bundles</CardDescription><CardTitle className="text-2xl">{incomplete.length}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">missing expected artifacts</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>Healthy bundles</CardDescription><CardTitle className="text-2xl">{rows.length - errors.length - incomplete.length}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">of {rows.length} runs</CardContent></Card>
      </div>
      {errors.length > 0 && (
        <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertTitle>Artifact errors detected</AlertTitle>
          <AlertDescription className="text-sm">{errors.map((x) => x.run.run_id).join(', ')} — these are surfaced, not hidden, and downgrade the evidence posture.</AlertDescription></Alert>
      )}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Per-run artifact inventory</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Run</TableHead><TableHead>Health</TableHead><TableHead>Present</TableHead><TableHead>Missing</TableHead><TableHead>Malformed</TableHead><TableHead>Empty</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map(({ run, h, cls }) => (
                <TableRow key={run.run_id}>
                  <TableCell className="font-mono text-xs">{run.run_id}</TableCell>
                  <TableCell><ResultBadge result={cls} /></TableCell>
                  <TableCell className="text-xs">{h?.present?.length ?? 0}/{h?.expected ?? '?'}</TableCell>
                  <TableCell className="text-xs text-red-600">{h?.missing?.length ? h.missing.join(', ') : '—'}</TableCell>
                  <TableCell className="text-xs text-fuchsia-700">{h?.malformed?.length ? h.malformed.join(', ') : '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{h?.empty?.length ? h.empty.join(', ') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============================================================ Historical trends */

export function TrendsTab() {
  const runs = useQaRuns();
  const findings = useQaFindings();
  const audit = useQaAuditDebt();
  const mutations = useQaMutations();
  if (runs.isLoading) return <Loading />;
  if (runs.error) return <ErrorState e={runs.error} />;
  if (!runs.data?.length) return <Empty what="run" />;

  // Result-over-time per comparable series (engine+profile+env+scale). Only ≥2 points shown.
  const series = comparableSeries(runs.data).filter((s) => s.sufficient);

  return (
    <div className="space-y-4">
      <Alert className="border-slate-300"><AlertTriangle className="h-4 w-4" />
        <AlertTitle>Trends use only comparable runs</AlertTitle>
        <AlertDescription className="text-sm">Runs are grouped by engine + profile + environment + scale; incompatible profiles are never plotted together. Series with a single observation are hidden as insufficient.</AlertDescription>
      </Alert>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card><CardHeader className="pb-1"><CardDescription>Findings ingested (point-in-time)</CardDescription><CardTitle className="text-2xl">{findings.data?.length ?? 0}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">no time-series of finding counts is captured across runs — single snapshot only</CardContent></Card>
        <Card><CardHeader className="pb-1"><CardDescription>Mutation kill score</CardDescription><CardTitle className="text-2xl">{mutations.data?.length ? `${mutations.data.filter((m) => m.killed).length}/${mutations.data.length}` : 'n/a'}</CardTitle></CardHeader><CardContent className="text-xs text-muted-foreground">audit debt items: {audit.data?.length ?? 0}</CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Result over time — comparable series</CardTitle></CardHeader>
        <CardContent>
          {!series.length ? <Insufficient what="comparable historical evidence" /> : (
            <div className="space-y-3">
              {series.map((s) => {
                const ordered = s.items.slice().sort((a, b) => (a.finished_at ?? '').localeCompare(b.finished_at ?? ''));
                return (
                  <div key={s.key} className="text-xs">
                    <div className="font-medium mb-1">{s.key.replace(/\|/g, ' · ')} <span className="text-muted-foreground">({s.items.length} runs)</span></div>
                    <div className="flex flex-wrap gap-1">
                      {ordered.map((r) => <span key={r.run_id} title={`${r.run_id} @ ${r.finished_at}`}><ResultBadge result={normResult(r.result)} compact /></span>)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">Latency/residue/NOT_EXECUTED-count trends require repeated same-profile runs; where fewer than two comparable observations exist the series is shown as insufficient rather than fabricated.</p>
    </div>
  );
}
